import { useTranslation } from 'react-i18next'
import {
  Button as AriaButton,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  Heading,
  RangeCalendar,
} from 'react-aria-components'
import { parseDate } from '@internationalized/date'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { dateRangePickerStyles as styles } from '@/components/base/date-range-picker'
import { cx } from '@/lib/utils/cx'

interface InboxDateRangeFilterProps {
  /** `YYYY-MM-DD`, or empty */
  from: string
  /** `YYYY-MM-DD`, or empty */
  to: string
  onChange: (from: string, to: string) => void
}

export function InboxDateRangeFilter({ from, to, onChange }: InboxDateRangeFilterProps) {
  const { t } = useTranslation()
  const value = from && to ? { start: parseDate(from), end: parseDate(to) } : null

  return (
    <>
      <RangeCalendar
        aria-label={t('components.date_picker.select_date_range')}
        value={value}
        // A value is reported only after the end date is chosen, so the first click does not query.
        onChange={(range) => range && onChange(range.start.toString(), range.end.toString())}
        className="px-2 pt-2 pb-1"
      >
        <header className={cx(styles.common.calendarHeader, 'mb-1')}>
          <AriaButton slot="previous" className={cx(styles.common.navButton, 'cursor-pointer')}>
            <ChevronLeft
              className="size-4 text-gray-700 dark:text-gray-300"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </AriaButton>
          <Heading className={styles.common.calendarHeading} />
          <AriaButton slot="next" className={cx(styles.common.navButton, 'cursor-pointer')}>
            <ChevronRight
              className="size-4 text-gray-700 dark:text-gray-300"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </AriaButton>
        </header>
        <CalendarGrid>
          <CalendarGridHeader>
            {(day) => (
              <CalendarHeaderCell className={cx(styles.common.weekdayHeader, 'size-8 text-xs')}>
                {day}
              </CalendarHeaderCell>
            )}
          </CalendarGridHeader>
          <CalendarGridBody>
            {(date) => (
              <CalendarCell date={date} className={cx(styles.common.calendarCell, 'size-8')} />
            )}
          </CalendarGridBody>
        </CalendarGrid>
      </RangeCalendar>
      {(from || to) && (
        <>
          <div className="h-px bg-gray-200 dark:bg-gray-700" />
          <button
            type="button"
            className="w-full cursor-pointer px-3 py-2 text-left text-sm leading-[18px] text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
            onClick={() => onChange('', '')}
          >
            {t('resources.generic.clear')}
          </button>
        </>
      )}
    </>
  )
}
